import type { CommunityNote } from "../api/community";
import type { MaterialItem } from "../api/library";

export type StoryMaterialSpec = {
  id: string;
  name: string;
  color: string;
  metalness: number;
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
  /** 仅使用后端签发的小尺寸贴图，避免首页拉取原图。 */
  mapUrl?: string;
};

/** 首页 3D 叙事从后台取用的数据：材质章节的四种材质，素材章节的三张参考图。 */
export type HomeStoryShowcase = {
  materials: StoryMaterialSpec[];
  references: string[];
};

const HEX = /^#?[0-9a-f]{6}$/i;

function normalizeHex(value?: string | null) {
  const text = (value || "").trim();
  if (!HEX.test(text)) return null;
  return (text.startsWith("#") ? text : `#${text}`).toLowerCase();
}

function finite(value: number | undefined, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;
}

function toSpec(item: MaterialItem): StoryMaterialSpec | null {
  const params = item.renderParams;
  const color = normalizeHex(params?.color) || normalizeHex(item.showcase?.colorHex);
  if (!color) return null;
  return {
    id: item.id,
    name: item.name,
    color,
    metalness: finite(params?.metalness, 0),
    roughness: finite(params?.roughness, 0.5),
    clearcoat: finite(params?.clearcoat, 0),
    clearcoatRoughness: finite(params?.clearcoatRoughness, 0.2),
    mapUrl: item.mapThumbUrl || undefined,
  };
}

/** 挑出颜色互不相同的材质，带小贴图的优先。 */
export function buildStoryShowcase(materials: MaterialItem[], notes: CommunityNote[]): HomeStoryShowcase {
  const specs = materials
    .filter((item) => item.enabled !== false)
    .map(toSpec)
    .filter((item): item is StoryMaterialSpec => item !== null)
    .sort((a, b) => Number(Boolean(b.mapUrl)) - Number(Boolean(a.mapUrl)));
  const seen = new Set<string>();
  const picked: StoryMaterialSpec[] = [];
  for (const spec of specs) {
    if (seen.has(spec.color)) continue;
    seen.add(spec.color);
    picked.push(spec);
    if (picked.length === 4) break;
  }
  const references = notes
    .map((note) => note.coverThumbUrl || "")
    .filter((url) => url.length > 0)
    .slice(0, 3);
  return { materials: picked.length === 4 ? picked : [], references };
}

export function isSameShowcase(a: HomeStoryShowcase | null, b: HomeStoryShowcase | null) {
  if (!a || !b) return a === b;
  return a.materials.map((item) => item.id).join() === b.materials.map((item) => item.id).join()
    && a.references.join() === b.references.join();
}
